import React from 'react';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { QuotaUser } from '../governedMcpTypes';
import { formatTokens } from '../quotaUsage';

function dayLabel(isoDate: string): string {
  const parsed = new Date(`${isoDate}T12:00:00`);
  if (Number.isNaN(parsed.getTime())) return isoDate;
  return parsed.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export default function QuotaSpendCharts({
  users,
  isLight,
  subtitle,
}: {
  users: QuotaUser[];
  isLight: boolean;
  subtitle?: string;
}) {
  const used = users.reduce((sum, item) => sum + item.tokensUsed, 0);
  const limit = users.reduce((sum, item) => sum + item.tokenLimit, 0);
  const remaining = Math.max(0, limit - used);
  const dailyMap = new Map<string, number>();
  const modelMap = new Map<string, number>();
  for (const user of users) {
    for (const point of user.dailyUsage) {
      dailyMap.set(point.date, (dailyMap.get(point.date) ?? 0) + point.tokens);
    }
    for (const row of user.byModel) {
      modelMap.set(row.model, (modelMap.get(row.model) ?? 0) + row.tokens);
    }
  }
  const daily = Array.from(dailyMap.entries())
    .sort(([left], [right]) => left.localeCompare(right))
    .map(([date, tokens]) => ({ date, tokens, label: dayLabel(date) }));
  const models = Array.from(modelMap.entries())
    .map(([model, tokens]) => ({ model, tokens }))
    .sort((left, right) => right.tokens - left.tokens);
  const allocation = [
    { name: 'Used', tokens: used, fill: isLight ? '#4f46e5' : '#818cf8' },
    { name: 'Remaining', tokens: remaining, fill: isLight ? '#cbd5e1' : '#3f3f46' },
  ];
  const modelColors = isLight
    ? ['#4f46e5', '#0ea5e9', '#7c3aed', '#059669']
    : ['#818cf8', '#38bdf8', '#a78bfa', '#34d399'];
  const axis = isLight ? '#94a3b8' : '#737373';
  const grid = isLight ? '#e2e8f0' : '#27272a';
  const tooltipStyle = {
    background: isLight ? '#ffffff' : '#171717',
    border: isLight ? '1px solid #e5e7eb' : '1px solid #3f3f46',
    borderRadius: 12,
    fontSize: 12,
    color: isLight ? '#171717' : '#f5f5f5',
  };

  return (
    <div className="space-y-4">
      <div className={`rounded-xl border p-4 ${isLight ? 'border-neutral-200 bg-white' : 'border-neutral-800 bg-neutral-900'}`}>
        <p className={`text-sm font-medium ${isLight ? 'text-neutral-900' : 'text-white'}`}>Tokens per day</p>
        <p className={`text-xs mt-0.5 mb-3 ${isLight ? 'text-neutral-500' : 'text-neutral-400'}`}>{subtitle || 'Current period'}</p>
        <div className="h-48">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={daily} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
              <CartesianGrid stroke={grid} strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="label" tick={{ fill: axis, fontSize: 11 }} axisLine={false} tickLine={false} />
              <YAxis
                tick={{ fill: axis, fontSize: 11 }}
                axisLine={false}
                tickLine={false}
                width={56}
                tickFormatter={(value: number) => (value >= 1000 ? `${Math.round(value / 1000)}k` : String(value))}
              />
              <Tooltip contentStyle={tooltipStyle} formatter={(value: number) => [formatTokens(value), 'Tokens']} />
              <Area
                type="monotone"
                dataKey="tokens"
                stroke={isLight ? '#4f46e5' : '#818cf8'}
                fill={isLight ? '#6366f1' : '#6366f1'}
                fillOpacity={isLight ? 0.16 : 0.28}
                strokeWidth={2}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <div className={`rounded-xl border p-4 ${isLight ? 'border-neutral-200 bg-white' : 'border-neutral-800 bg-neutral-900'}`}>
          <p className={`text-sm font-medium ${isLight ? 'text-neutral-900' : 'text-white'}`}>Spend vs limit</p>
          <p className={`text-xs mt-0.5 mb-3 ${isLight ? 'text-neutral-500' : 'text-neutral-400'}`}>Used and remaining tokens</p>
          <div className="h-40">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={allocation} layout="vertical" margin={{ top: 4, right: 12, left: 8, bottom: 0 }}>
                <XAxis
                  type="number"
                  tick={{ fill: axis, fontSize: 11 }}
                  axisLine={false}
                  tickLine={false}
                  tickFormatter={(value: number) => (value >= 1000 ? `${Math.round(value / 1000)}k` : String(value))}
                />
                <YAxis type="category" dataKey="name" tick={{ fill: axis, fontSize: 11 }} axisLine={false} tickLine={false} width={78} />
                <Tooltip contentStyle={tooltipStyle} formatter={(value: number) => [formatTokens(value), 'Tokens']} />
                <Bar dataKey="tokens" radius={[6, 6, 6, 6]} barSize={18}>
                  {allocation.map((entry) => (
                    <Cell key={entry.name} fill={entry.fill} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className={`rounded-xl border p-4 ${isLight ? 'border-neutral-200 bg-white' : 'border-neutral-800 bg-neutral-900'}`}>
          <p className={`text-sm font-medium ${isLight ? 'text-neutral-900' : 'text-white'}`}>By model</p>
          <p className={`text-xs mt-0.5 mb-3 ${isLight ? 'text-neutral-500' : 'text-neutral-400'}`}>Current period breakdown</p>
          <div className="h-40">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={models} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid stroke={grid} strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="model" tick={{ fill: axis, fontSize: 10 }} axisLine={false} tickLine={false} interval={0} angle={-16} textAnchor="end" height={48} />
                <YAxis
                  tick={{ fill: axis, fontSize: 11 }}
                  axisLine={false}
                  tickLine={false}
                  width={48}
                  tickFormatter={(value: number) => (value >= 1000 ? `${Math.round(value / 1000)}k` : String(value))}
                />
                <Tooltip contentStyle={tooltipStyle} formatter={(value: number) => [formatTokens(value), 'Tokens']} />
                <Bar dataKey="tokens" radius={[6, 6, 0, 0]} barSize={22}>
                  {models.map((entry, index) => (
                    <Cell key={entry.model} fill={modelColors[index % modelColors.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
}
